import fetch from 'isomorphic-fetch';

import { keepAliveAgent } from './keepAliveAgent';

export interface FetchWithTimeoutOptions extends RequestInit {
  // Not part of the DOM RequestInit type, but honoured by node-fetch on the
  // server. Left unset, server-side requests get the shared keep-alive agent
  // above; set it to use a custom agent (proxy, mTLS, custom CA) instead.
  agent?: unknown;
  timeout?: number;
}

export async function fetchWithTimeout(
  url: string,
  options: FetchWithTimeoutOptions = {},
): Promise<Response> {
  const { timeout, ...optionsWithoutTimeout } = options;

  const fetchOptions: FetchWithTimeoutOptions =
    keepAliveAgent && !optionsWithoutTimeout.agent
      ? { ...optionsWithoutTimeout, agent: keepAliveAgent }
      : optionsWithoutTimeout;

  if (!timeout) {
    return fetch(url, fetchOptions);
  }

  const timeoutSignal = AbortSignal.timeout(timeout);
  fetchOptions.signal = timeoutSignal as NonNullable<RequestInit['signal']>;

  return fetch(url, fetchOptions).catch((error) => {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      const message = `${url} took too long to respond`;
      throw new Error(message);
    }

    throw error;
  });
}

export interface FetchJSONWithTimeoutOptions extends Omit<FetchWithTimeoutOptions, 'body'> {
  body?: Record<string, unknown>;
}

export const fetchJSONWithTimeout = (
  url: string,
  options: FetchJSONWithTimeoutOptions = {},
): Promise<Response> => {
  const { body, headers, ...fetchOptions } = options;

  return fetchWithTimeout(url, {
    ...fetchOptions,
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    ...(body && { body: JSON.stringify(body) }),
  });
};
